import { useMutation } from "@apollo/client/react";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import {
  PrepareEventNormalizationImportDocument,
  type PrepareEventNormalizationImportMutation,
  type PrepareEventNormalizationImportMutationVariables,
} from "~/features/dataImportProjects/gqls";
import { useTenant } from "~/features/tenant/context";
import { translateError } from "~/lib/errors";

interface PrepareEventNormalizationImportInput {
  name: string;
  description: string;
  referenceId: string;
  file: File;
}

export function usePrepareEventNormalizationImport() {
  const { t } = useTranslation();
  const { tenantId } = useTenant();
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [prepareMutation] = useMutation<
    PrepareEventNormalizationImportMutation,
    PrepareEventNormalizationImportMutationVariables
  >(PrepareEventNormalizationImportDocument);

  const prepare = useCallback(
    async (input: PrepareEventNormalizationImportInput): Promise<string | null> => {
      if (!tenantId || processing) return null;

      setProcessing(true);
      setError(null);

      try {
        const result = await prepareMutation({
          variables: {
            input: {
              tenantId,
              referenceId: input.referenceId,
              name: input.name.trim(),
              description: input.description.trim(),
              file: input.file,
            },
          },
        });
        const response = result.data?.cnutPrepareEventNormalizationImport;

        if (response?.__typename === "PrepareEventNormalizationImportOutput") {
          toast.success(t("feature.dataImportProjects.eventNormalizationImport.success"));
          return response.projectId;
        }

        if (response?.__typename === "ValidationError") {
          const message =
            response.fields
              .map((field) => field.message)
              .filter(Boolean)
              .join("\n") || response.message;
          setError(message);
          toast.error(message);
          return null;
        }

        if (response?.__typename === "ClientError") {
          const message = response.message;
          setError(message);
          toast.error(message);
          return null;
        }

        throw new Error(t("feature.dataImportProjects.eventNormalizationImport.invalidResponse"));
      } catch (cause) {
        const message = translateError(t, cause);
        setError(message);
        toast.error(message);
        return null;
      } finally {
        setProcessing(false);
      }
    },
    [prepareMutation, processing, t, tenantId],
  );

  return { prepare, processing, error, clearError: () => setError(null) };
}
